import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Clock } from 'lucide-react';
import type { AppointmentListItem } from '@/hooks/useAppointments';
import { TYPE } from '@/lib/typography';
import {
  calendarBlockTitle,
  countedPatientAppointments,
  isAllDayCalendarBlock,
  isCalendarBlock,
} from '@/utils/calendarTimeBlock';
import {
  assertNever,
  formatHourPeriods,
  type ClinicHoursException,
  type ClinicHoursExceptionType,
  type ClinicHoursSource,
  type HourPeriod,
} from '@/lib/clinicHours';
import {
  CLINIC_TIMEZONE,
  clinicDayKey,
  clinicDayNumber,
  clinicMonthGridDays,
  clinicMonthKey,
  sameClinicDay,
} from '@/utils/calendarClinicTime';
import { formatAppointmentClock } from '@/utils/appointmentTimeFormat';

interface MonthResolvedHours {
  enabled: boolean;
  periods: HourPeriod[];
  source?: ClinicHoursSource;
  exceptionType?: ClinicHoursExceptionType | null;
}

interface MonthViewProps {
  anchor: Date;
  appointments: AppointmentListItem[];
  onDayClick: (date: Date) => void;
  onAppointmentClick?: (id: string) => void;
  hourExceptions?: Map<string, ClinicHoursException>;
  resolvedHoursByDate?: Map<string, MonthResolvedHours | null>;
  onDayHoursClick?: (date: Date) => void;
}

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

function monthExceptionCaption(exception: ClinicHoursException, t: TFunction): string {
  switch (exception.exception_type) {
    case 'closed':
      return t('appointments.day_hours.indicator_closed');
    case 'custom_hours': {
      const hours = formatHourPeriods(exception.periods);
      return hours
        ? t('appointments.day_hours.indicator_open', { hours })
        : t('appointments.day_hours.indicator_custom');
    }
    default:
      return assertNever(exception.exception_type);
  }
}

export function MonthView({
  anchor,
  appointments,
  onDayClick,
  onAppointmentClick,
  hourExceptions,
  resolvedHoursByDate,
  onDayHoursClick,
}: MonthViewProps) {
  const { t, i18n } = useTranslation();
  const today = useMemo(() => new Date(), []);

  const days = useMemo(() => clinicMonthGridDays(anchor), [anchor]);
  const anchorMonth = clinicMonthKey(anchor);

  const appointmentsByDay = useMemo(() => {
    const map = new Map<string, AppointmentListItem[]>();
    for (const apt of appointments) {
      const key = clinicDayKey(new Date(apt.starts_at));
      const list = map.get(key);
      if (list) list.push(apt);
      else map.set(key, [apt]);
    }
    return map;
  }, [appointments]);

  function dayKey(d: Date) {
    return clinicDayKey(d);
  }

  function getDayAppointments(d: Date): AppointmentListItem[] {
    return appointmentsByDay.get(dayKey(d)) ?? [];
  }

  return (
    <div className="min-w-0 overflow-hidden rounded-card bg-ds-card shadow-card">
      {/* Weekday header row */}
      <div className="grid grid-cols-7 border-b border-ds-line bg-ds-card">
        {WEEKDAY_KEYS.map((key) => (
          <div
            key={key}
            className="border-r border-ds-line py-s2 text-center text-sm font-medium text-ds-muted last:border-r-0"
          >
            {t(`appointments.weekdays_short.${key}`)}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const dayAppointments = getDayAppointments(day);
          const dayCount = countedPatientAppointments(dayAppointments).length;
          const isToday = sameClinicDay(day, today);
          const isCurrentMonth = clinicMonthKey(day) === anchorMonth;
          const iso = clinicDayKey(day);
          const resolved = resolvedHoursByDate?.get(iso);
          const dayException = hourExceptions?.get(iso);
          const exceptionLabel =
            resolved?.source === 'exception'
              ? resolved.exceptionType === 'closed' || !resolved.enabled
                ? t('appointments.day_hours.indicator_closed')
                : formatHourPeriods(resolved.periods)
                  ? t('appointments.day_hours.indicator_open', {
                      hours: formatHourPeriods(resolved.periods),
                    })
                  : t('appointments.day_hours.indicator_custom')
              : dayException
                ? monthExceptionCaption(dayException, t)
                : null;

          return (
            <div
              key={day.toISOString()}
              className={`hover:bg-ds-teal-tint/40 group relative min-h-[100px] min-w-0 border-b border-r border-ds-line p-s2 text-left transition-colors last:border-r-0 ${
                !isCurrentMonth ? 'bg-ds-sunk' : ''
              }`}
              onContextMenu={(e) => {
                if (!onDayHoursClick) return;
                e.preventDefault();
                onDayHoursClick(day);
              }}
            >
              <button
                type="button"
                className="absolute inset-0 z-0"
                onClick={() => onDayClick(day)}
                aria-label={day.toLocaleDateString(i18n.language === 'da' ? 'da-DK' : 'en-US', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  timeZone: CLINIC_TIMEZONE,
                })}
              />
              <div className="pointer-events-none relative z-10 mb-1 flex items-start justify-between gap-1">
                <span
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-medium ${
                    isToday
                      ? 'bg-ds-teal text-ds-on-accent'
                      : isCurrentMonth
                        ? 'text-ds-text'
                        : 'text-ds-muted'
                  }`}
                >
                  {clinicDayNumber(day)}
                </span>
                <div className="flex items-center gap-1">
                  {exceptionLabel && (
                    <span
                      className={`${TYPE.caption} max-w-[7rem] truncate text-ds-teal`}
                      data-testid="month-hours-indicator"
                      title={exceptionLabel}
                    >
                      {exceptionLabel}
                    </span>
                  )}
                  {onDayHoursClick && (
                    <button
                      type="button"
                      data-testid="month-day-hours"
                      className="pointer-events-auto inline-flex h-6 w-6 items-center justify-center rounded-md text-ds-muted opacity-0 hover:bg-ds-card hover:text-ds-teal focus:opacity-100 focus:outline-none group-hover:opacity-100"
                      aria-label={t('appointments.day_hours.open_for_date', {
                        date: clinicDayNumber(day),
                      })}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDayHoursClick(day);
                      }}
                    >
                      <Clock className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {dayCount > 0 && (
                    <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-ds-teal px-1.5 text-xs font-medium text-ds-on-accent">
                      {dayCount}
                    </span>
                  )}
                </div>
              </div>

              {/* Show up to 3 appointment indicators */}
              <div className="pointer-events-none relative z-10 space-y-1">
                {dayAppointments.slice(0, 3).map((apt) => {
                  const start = new Date(apt.starts_at);
                  const timeStr = formatAppointmentClock(start, i18n.language, CLINIC_TIMEZONE);
                  return (
                    <button
                      key={apt.id}
                      type="button"
                      data-testid={
                        isAllDayCalendarBlock(apt) ? 'calendar-month-all-day' : undefined
                      }
                      className="pointer-events-auto relative z-20 block w-full truncate rounded border border-ds-line-strong bg-ds-card px-1.5 py-0.5 text-left text-xs leading-none text-ds-text"
                      style={{
                        borderLeft: `4px solid ${
                          apt.status === 'cancelled' ? 'var(--line-strong)' : 'var(--teal-line)'
                        }`,
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onAppointmentClick?.(apt.id);
                      }}
                    >
                      {isCalendarBlock(apt)
                        ? calendarBlockTitle(apt)
                        : `${timeStr} ${apt.patient_name}`}
                      {apt.course_badge
                        ? ` · ${t('appointments.course_badge', {
                            n: apt.course_badge.index,
                            total: apt.course_badge.total,
                          })}`
                        : ''}
                    </button>
                  );
                })}
                {dayAppointments.length > 3 && (
                  <div className="text-xs text-ds-muted">
                    +{dayAppointments.length - 3} {i18n.language === 'da' ? 'mere' : 'more'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
